// tv/shell.jsx — full-takeover TV / Apple TV remote surface.
//
// PR T1 of the TV-controls refactor. Replaces the centered-modal
// AMediaModal as the entry point for "I want to control this room's
// screen". The body is a faithful port of AMediaModal's Apple TV
// remote: now-playing strip, keyboard input bar, top + bottom chrome
// rows, gesture touchpad, volume rockers, destinations chips, audio
// settings panel. Subsequent PRs add TV-native controls (power /
// source / app — T2) and retire AMediaModal (T3).
//
// Mirrors music/shell.jsx in shape: full-takeover at z=60, paper
// background, header with back chevron + eyebrow + serif title, ESC
// to close, room-first (focusRoomId required — TV is inherently room-
// scoped per user decision 2026-05-24, no drawer entry).

import * as React from 'react';
import { I, CoverArt, AppleTvMark } from '../shared.jsx';
import { A_FONT, ALabel, useImageBlobUrl, fs, tvBrandFor, TakeoverShell, useScreenPower, Card, PowerButton, BusyDot } from '../direction-a.jsx';
import { RADIUS } from '../theme.js';
import { ChromeBtn, SkipKey, renderRocker, AudioSliderRow, BrandBadge } from './primitives.jsx';
import { SourcePicker } from './source-picker.jsx';
import { TVMenuDisclosure } from './tv-menu.jsx';
import { dispatchTVRemote } from './remote-config.js';
import { SonosRoomsChip, SonosRoomsPicker, readTvToSonos } from './sonos-rooms.jsx';
import { Scrubber } from '../music/primitives.jsx';

const { useEffect, useState, useRef } = React;

// How long the keyboard bar outlives a dropped focus. tvOS ends and
// restarts its text session within seconds while a field is on screen
// (11-31 s spells, observed 2026-10-30), so hiding the bar the instant
// focus drops would take it away mid-word.
const KB_HOLD_MS = 3000;

// Typing pause before the bar sends. Each send carries the whole box,
// so a burst of keys goes out as one set_keyboard_text call.
const KB_DEBOUNCE_MS = 200;

// ── Touchpad gesture tuning ────────────────────────────────────────────
//
// One nav press per STEP_PX crossed (unchanged from the original pad).
// The scrub reuses STEP_PX as its unit so a swipe feels the same length
// in either mode.
const STEP_PX = 60;

// Long-press. HOLD_RING_MS is when the ring starts drawing, HOLD_MS
// when the held press actually goes out: a tap is under 350 ms, so the
// gap keeps the ring off every ordinary tap. HOLD_SLOP_PX cancels the
// hold the moment the finger travels, since a swipe is not a hold.
//
// The ring's duration is OUR choice and says nothing about the TV.
// hold_secs >= 1 makes HA send one pyatv InputAction.Hold and return;
// it does not hold the button down for a wall-clock second. See
// sendRemoteCommand in hooks/use-panel-rooms.jsx.
//
// SLOP was 10 px and that was too tight: a finger resting on glass
// drifts further than that, so the hold cancelled before it fired
// anywhere the user was already mid-interaction. Ben, 2026-11-01: it
// worked on the home screen and "doesn't seem to work consistently"
// elsewhere. 20 px is still well inside the 60 px that starts a swipe.
const HOLD_RING_MS = 200;
const HOLD_MS = 600;
const HOLD_SLOP_PX = 20;

// A playing session this short is a trailer or a preview loop, not
// something anyone wants transport controls for. Measured at the
// Pavilion 2026-11-01: the Apple TV app's home screen autoplays
// trailers (duration 26, empty title) and HBO Max loops a 30 s
// preview, and both made the Apple TV report `playing`, which put a
// Playback row under the pad for a trailer. The cost is a genuinely
// short clip (a YouTube short) losing its row; the edge arrows still
// skip it.
const PREVIEW_MAX_S = 90;

// Skip sizes offered where the app publishes no timeline, in multiples
// of the app's own step. On Kayo, whose step is 10 s, that is 10 s,
// a minute and five minutes — the five-minute pair at Ben's request,
// 2026-11-01, live sport being the case that needs it. Each multiple
// is a separate remote command, so 30 of them takes a second or two to
// play out and an app that coalesces them will land short. The glyphs
// carry no seconds for the same reason the caption does: the step
// belongs to the app.
const SKIP_STEPS = [1, 6, 30];

// Did the seek take? No app tells us, so the only check is to look at
// the position again afterwards. SEEK_TOLERANCE_S is wide enough that
// playback continuing for the settle window doesn't read as a miss.
//
// The check is sampled TWICE, because a single sample cannot tell "the
// app ignored the seek" from "HA hasn't pushed the new position yet" —
// both look like a stale position. Two samples that agree make the
// second reading much less likely. It is still not proof, which is why
// the notice names the app rather than the panel and offers the
// long-press menu as the way round.
const SEEK_SETTLE_MS = 2500;
const SEEK_TOLERANCE_S = 5;
const SEEK_NOTICE_MS = 9000;

// Primary volume entity for a TV-bearing room. Amp-primary for
// Pavilion / Rumpus (their Sonos feeds the external amp via line-in,
// so amp volume is what's audible); Sonos-primary for Office (Arc IS
// the amp). Mirrors AMediaModal's same routing block — keep the two
// in lockstep until AMediaModal is fully retired.
function primaryEntityFor(room) {
  if (!room) return null;
  if (room.av) {
    if (room.id === 'pavilion') return 'media_player.drx_3_1';
    if (room.id === 'rumpus')   return 'media_player.drx_5';
  }
  return ({
    office:         'media_player.office',
    pavilion:       'media_player.pavilion',
    rumpus:         'media_player.rumpus',
    master_bed:     'media_player.master_bed',
    master_ensuite: 'media_player.ensuite',
    pool:           'media_player.pool',
  })[room.id] || null;
}

function primaryLabelFor(room) {
  if (!room) return '';
  if (room.av) {
    if (room.id === 'pavilion') return 'DRX-3.1';
    if (room.id === 'rumpus')   return 'DRX-5';
  }
  return room.sonos?.room || 'Sonos';
}

export function TVShell({
  T, open, onClose, rooms, focusRoomId, viewport, hass,
  mediaPlayer, sendRemoteCommand, appleTvCommand, appleTvText,
  audioCommand, eqCommand, act, update, sonosSpeakerCommand,
  destinationsCommand, togglePoolExtension, poolExtension,
}) {
  // ── All hooks declared BEFORE early-returns ──────────────────────────────
  // (React requires identical hook counts across renders; early-returns
  // must come after every useState / useEffect / useRef.)

  // Reset audio-panel + keyboard state when shell opens / closes.
  const [audioOpen, setAudioOpen] = useState(false);
  const [kbHold, setKbHold] = useState(false);
  const [kbText, setKbText] = useState('');
  const [pressed, setPressed] = useState(null);
  const [ripple, setRipple] = useState(null);
  // PR T2 — source picker overlay + TV menu disclosure (TV-native
  // navigation, brand-mapped command names per room).
  const [sourcePickerOpen, setSourcePickerOpen] = useState(false);
  const [tvMenuOpen, setTvMenuOpen] = useState(false);
  // TV audio to other Sonos rooms (tv/sonos-rooms.jsx). Pending lives
  // here, not in the picker, so the chip keeps its busy treatment if
  // the picker is closed mid-send.
  const [sonosRoomsOpen, setSonosRoomsOpen] = useState(false);
  const [sonosRoomsPending, setSonosRoomsPending] = useState(null);

  // BRAVIA mode override — when the user taps a mode pill, snap the
  // panel into the requested mode immediately rather than waiting for
  // the braviatv integration's next poll to surface the actual TV
  // source. Cleared once HA's state matches the override (or after a
  // safety-net timeout). See block below shell render for context.
  const [modeOverride, setModeOverride] = useState(null);

  // Touchpad long-press feedback. `holdPhase` is null, 'arming' (ring
  // drawing) or 'held' (the held press went out).
  const [holdPhase, setHoldPhase] = useState(null);
  // "That didn't take" copy for a seek the app ignored. Null most of
  // the time; a successful seek says nothing, per the house rule that
  // success never notifies.
  const [seekNotice, setSeekNotice] = useState(null);

  const padRef = useRef(null);
  const startRef = useRef(null);
  const holdRef = useRef({ ring: null, fire: null });
  // Latest Apple TV overlay, readable from inside a timer without
  // making the timer depend on the render that scheduled it.
  const atvRef = useRef(null);
  const seekCheckRef = useRef({ check: null, expire: null });
  const kbInputRef = useRef(null);
  // Live typing. `want` is the box's latest text, `sent` the last text
  // handed to HA. One call is in flight at a time and each carries the
  // whole box, so backspace and corrections stay in step and a call
  // landing late is overwritten by the next one.
  const kbSendRef = useRef({ timer: null, inFlight: false, want: null, sent: null });

  // Lookups (safe with null room — hooks below tolerate missing fields).
  const room = focusRoomId ? rooms?.find((r) => r.id === focusRoomId) : null;
  const tv = room?.tv;
  const appleTv = room?.appleTv;
  const audio = room?.audio;
  // Tone comes from room.eq, not room.audio — it is resolved against
  // whichever device owns EQ in this room's chain (the Integra amp here,
  // the Sonos in the pure-Sonos rooms), which is not always the device
  // that owns volume. Centre and surround stay on room.audio: they are
  // amp-specific extras with no cross-room equivalent.
  const eq = room?.eq;
  const hasAudio = !!((audio && (audio.centre || audio.surround)) || eq?.bass || eq?.treble);
  // The keyboard bar follows the Apple TV's text focus: it appears when
  // a field opens and stays KB_HOLD_MS after focus drops. There is no
  // manual open; with no focus, set_keyboard_text has nothing to type
  // into.
  const keyboardFocused = !!room?.keyboardFocus;
  const kbOpen = keyboardFocused || kbHold;
  const appleTvCoverUrl = useImageBlobUrl(appleTv?.mediaImage);

  // TV audio to other Sonos rooms — null outside the three Apple TV
  // rooms or before ha-av's sensor exists. The "Playing in" row now
  // renders for Office too, which has no amp destinations.
  const tvToSonos = readTvToSonos(hass, room?.id);
  const hasDestRow = !!((room?.destinations?.length && destinationsCommand) || tvToSonos);
  useEffect(() => { if (!open) setSonosRoomsOpen(false); }, [open]);

  // Drop any in-flight hold timers when the shell closes or unmounts —
  // otherwise a hold armed just before close fires at a shell that is
  // no longer on screen.
  useEffect(() => {
    if (open) return;
    clearTimeout(holdRef.current.ring);
    clearTimeout(holdRef.current.fire);
    holdRef.current = { ring: null, fire: null };
    startRef.current = null;
    setHoldPhase(null);
  }, [open]);
  useEffect(() => () => {
    clearTimeout(holdRef.current.ring);
    clearTimeout(holdRef.current.fire);
    clearTimeout(seekCheckRef.current.check);
    clearTimeout(seekCheckRef.current.expire);
  }, []);

  // Keep the timer-readable copy of the overlay current. MUST live up
  // here with the other hooks: the seek check reads it from inside a
  // setTimeout, but this file early-returns for a missing room or
  // screen, and a hook below those returns changes the hook count
  // between renders.
  useEffect(() => { atvRef.current = room?.appleTv || null; }, [room?.appleTv]);

  // Clear a stale "didn't take" when the shell closes, the room
  // changes, or the Apple TV moves to different content — the notice
  // was about one press on one programme.
  useEffect(() => {
    setSeekNotice(null);
    clearTimeout(seekCheckRef.current.check);
  }, [open, focusRoomId, room?.appleTv?.title]);

  // Whole-stack power — the same hook the room tile's Screen toggle
  // uses, so the two controls that fire script.<room>_watch / _off share
  // one awake read, one settle read and one in-flight affordance. Must
  // sit up here with the other hooks: `room` is null until a room is
  // focused and the early-returns below would otherwise change the hook
  // count between renders. The hook tolerates a null room.
  const {
    awake: screenAwake, busy: powerBusy, slow: powerSlow,
    intent: powerIntent, press: pressPower, label: powerLabel,
  } = useScreenPower({ room, act, update });

  useEffect(() => {
    if (keyboardFocused) { setKbHold(true); return; }
    const t = setTimeout(() => setKbHold(false), KB_HOLD_MS);
    return () => clearTimeout(t);
  }, [keyboardFocused]);

  // Clear local kb buffer + auto-focus the input on each open. A new
  // focus session starts with nothing sent, so the first key always goes.
  useEffect(() => {
    setKbText('');
    const s = kbSendRef.current;
    clearTimeout(s.timer);
    kbSendRef.current = { timer: null, inFlight: false, want: null, sent: null };
  }, [kbOpen]);
  useEffect(() => { if (kbOpen) kbInputRef.current?.focus(); }, [kbOpen]);

  const flushKb = (roomId) => {
    const s = kbSendRef.current;
    clearTimeout(s.timer);
    s.timer = null;
    if (s.inFlight || s.want === null || s.want === s.sent || !appleTvText) return;
    const text = s.want;
    s.inFlight = true;
    Promise.resolve(appleTvText(roomId, text ? 'set' : 'clear', text)).finally(() => {
      s.inFlight = false;
      s.sent = text;
      if (kbSendRef.current === s) flushKb(roomId);
    });
  };
  const queueKb = (roomId, text) => {
    const s = kbSendRef.current;
    s.want = text;
    clearTimeout(s.timer);
    s.timer = setTimeout(() => flushKb(roomId), KB_DEBOUNCE_MS);
  };

  // Pre-warm pyatv MRP session on shell open — first command after a
  // cold open otherwise takes 1-3s while the handshake re-establishes.
  // `wakeup` is a no-op when the Apple TV is already awake.
  useEffect(() => {
    if (!open || !room?.id) return;
    sendRemoteCommand?.(room.id, 'wakeup');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, room?.id]);

  // Reset transient state on shell open (close → open should look fresh,
  // not carry forward an open audio panel or stale ripple).
  useEffect(() => {
    if (open) {
      setAudioOpen(false);
      setKbText('');
      setPressed(null);
      setRipple(null);
      setSourcePickerOpen(false);
      setTvMenuOpen(false);
      setModeOverride(null);
    }
  }, [open]);

  // Clear modeOverride once HA's live state matches the override —
  // i.e. braviatv has polled the TV and surfaced the new source. Safe
  // to run before early-returns; no-ops when room or hass isn't ready.
  useEffect(() => {
    if (modeOverride === null) return;
    if (!focusRoomId) return;
    const tv2Attrs = hass?.states?.[`media_player.${focusRoomId}_tv_2`]?.attributes || {};
    const detectedMode = (
      tv2Attrs.media_content_type === 'channel'
      || tv2Attrs.media_channel != null
    ) ? 'freetv' : 'appletv';
    if (detectedMode === modeOverride) setModeOverride(null);
  }, [hass, focusRoomId, modeOverride]);

  // Safety-net timeout — clear modeOverride after 12s even if HA never
  // catches up, so the segment doesn't get stuck. 12s comfortably
  // exceeds braviatv's poll interval (~10s).
  useEffect(() => {
    if (modeOverride === null) return;
    const id = setTimeout(() => setModeOverride(null), 12000);
    return () => clearTimeout(id);
  }, [modeOverride]);

  // Early-return — after all hooks above. Hook-count stable.
  if (!open) return null;
  if (!room) {
    // Defensive — TVShell is opened from a room tile that knows it has
    // a TV, but if focusRoomId resolves to nothing (race during route
    // change, etc.) render an empty shell with just the back chevron
    // rather than crashing.
    return (
      <TakeoverShell T={T} viewport={viewport} eyebrow="Remote" title="—" onClose={onClose} bodyless>
        <div style={{
          padding: 40, textAlign: 'center', color: T.inkSoft,
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
        }}>
          No room selected.
        </div>
      </TakeoverShell>
    );
  }

  if (!tv) {
    return (
      <TakeoverShell T={T} viewport={viewport} eyebrow="Remote" title={room.name} onClose={onClose} bodyless>
        <div style={{
          padding: 40, textAlign: 'center', color: T.inkSoft,
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
        }}>
          No screens in this room.
        </div>
      </TakeoverShell>
    );
  }

  const isOn = screenAwake;

  // Visual + dispatch separation — UI flashes immediately, the network
  // call goes out independently.
  const press = (k) => { setPressed(k); setTimeout(() => setPressed(null), 220); };
  const dispatch = (cmd) => {
    if (!isOn) return;
    press(cmd);
    sendRemoteCommand?.(room.id, cmd);
  };

  // ── Apple-TV ↔ Free-TV mode (BRAVIA rooms only) ──────────────────────
  //
  // Sony BRAVIA TVs have a built-in tuner; we expose Apple-TV vs
  // Free-TV controls as a segmented mode toggle. Mode follows whatever
  // the TV is actually showing — when the user picks a segment, we
  // fire the BRAVIA source change; when they change source via the
  // physical remote, the segment reflects the new state.
  //
  // Detection combines multiple signals because BRAVIA models report
  // the tuner differently:
  //   - Rumpus reports current_activity = 'com.sony.dtv.tvx'
  //   - Office reports current_activity = 'com.android.tv' even when
  //     on the tuner — the foreground Android app on Office's TV is
  //     the generic Android TV launcher rather than Sony's dedicated
  //     tuner app. Verified 2026-05-27 by direct state inspection.
  // So we also look at the braviatv media_player entity, which reports
  // media_content_type='channel' and a media_channel value when on the
  // tuner regardless of model. Either signal counts as "Free TV".
  //
  // Local modeOverride layered on top: when the user taps a pill we
  // snap the panel into that mode immediately, because braviatv polls
  // the TV every ~10s and live state would otherwise lag noticeably
  // behind the tap. The override clears once HA state matches (or
  // after a 12s safety-net timeout if state never catches up).
  //
  // Mode switch:
  //   Apple TV → media_player.select_source on the braviatv entity
  //              (`media_player.<room>_tv_2`) with the Apple-TV input
  //              name ("Apple TV" on Rumpus, "HDMI 1" on Office).
  //   Free TV  → remote.send_command on the braviatv remote entity
  //              (`remote.<room>_tv_2`) with Sony IRCC `Tv`. The
  //              superficially-similar `TvInput` cycles inputs (acts
  //              as the Input toggle button) instead of jumping to
  //              the tuner — verified the wrong way 2026-05-27 when
  //              pressing the panel's Free TV pill landed the TV on
  //              "next source" rather than the tuner.
  //
  // FTA controls (channel up/down, digit keys, Guide, Info) all go
  // through the same braviatv remote entity with Sony IRCC vocabulary
  // (PascalCase: ChannelUp, ChannelDown, Num0..Num9, Guide, Info).
  // androidtv_remote treats unknown commands as deep-link attempts and
  // surfaces "Item not found" on the BRAVIA launcher (see TV_REMOTE
  // mappings in remote-config.js), so we go via the braviatv entity
  // for these tuner-related operations.
  const isBravia = room.id === 'office' || room.id === 'rumpus';
  const tv2State = hass?.states?.[`media_player.${room.id}_tv_2`];
  const tv2Attrs = tv2State?.attributes || {};
  const tv2SourceList = tv2Attrs.source_list || [];
  // Live-detected mode: Free TV when the braviatv media_player reports a
  // tuner channel. These clear when the input changes / TV is off, so they
  // track the real input.
  //   - braviatv reports media_content_type 'channel'
  //   - braviatv has a non-null media_channel value
  // NOT used: `remote.<room>_tv` current_activity. It's the last-foreground
  // Android app and is STICKY — on Rumpus the Apple TV is an external HDMI
  // box, so current_activity stays 'com.sony.dtv.tvx' even on Apple TV and
  // when off, which forced the mode to Free TV and flipped it back ~12s
  // after the user picked Apple TV (the override safety-net). Office never
  // matched this signal anyway (it reports 'com.android.tv'). Removed
  // 2026-06-14.
  const detectedMode = (
    tv2Attrs.media_content_type === 'channel'
    || tv2Attrs.media_channel != null
  ) ? 'freetv' : 'appletv';
  const tvMode = modeOverride ?? detectedMode;

  const appleTvSourceName = tv2SourceList.includes('Apple TV') ? 'Apple TV' : 'HDMI 1';
  const switchToAppleTv = () => {
    setModeOverride('appletv');
    hass?.callService('media_player', 'select_source', {
      entity_id: `media_player.${room.id}_tv_2`,
      source: appleTvSourceName,
    });
  };
  const switchToFreeTv = () => {
    setModeOverride('freetv');
    hass?.callService('remote', 'send_command', {
      entity_id: `remote.${room.id}_tv_2`,
      command: 'Tv',
    });
  };
  const ftaCommand = (cmd) => {
    // Press feedback uses the IRCC code as the key — same `pressed`
    // state the chrome buttons use, so the conditional style on each
    // FTA button can light up briefly on tap. Matches the press
    // language established elsewhere in the shell (user feedback
    // 2026-05-27).
    press(cmd);
    hass?.callService('remote', 'send_command', {
      entity_id: `remote.${room.id}_tv_2`,
      command: cmd,
    });
  };
  // Live channel info for the FTA card. Different BRAVIA firmwares
  // populate media_channel + media_title inconsistently:
  //   Rumpus: media_channel="10",        media_title="Good Chef Bad Chef"
  //   Office: media_channel="9HD%20Sydney" (URL-encoded broadcaster name),
  //           media_title="90"            (channel number)
  // So we detect which is which by checking whether the value is
  // numeric. Office also doesn't expose EPG (media_duration = -1)
  // — the progress-bar conditional already handles that gracefully.
  const m_channel = tv2State?.attributes?.media_channel;
  const m_title   = tv2State?.attributes?.media_title;
  const isNumeric = (v) => v != null && /^\d+(\.\d+)?$/.test(String(v).trim());
  const channelIsNumeric = isNumeric(m_channel);
  const titleIsNumeric = isNumeric(m_title);
  let ftaChannel, ftaTitle;
  if (channelIsNumeric && !titleIsNumeric) {
    // Rumpus-style: media_channel holds the number, media_title the program name.
    ftaChannel = m_channel;
    ftaTitle = m_title;
  } else if (!channelIsNumeric && titleIsNumeric) {
    // Office-style: media_title holds the number, media_channel the broadcaster name.
    ftaChannel = m_title;
    ftaTitle = m_channel;
  } else {
    // Ambiguous (both or neither numeric) — fall back to direct mapping.
    ftaChannel = m_channel;
    ftaTitle = m_title;
  }
  // The Office BRAVIA URL-encodes spaces ("9HD%20Sydney"); decode for
  // display. decodeURIComponent throws on malformed input, so guard it.
  try { if (ftaTitle) ftaTitle = decodeURIComponent(ftaTitle); }
  catch { /* leave as-is if not valid URI encoding */ }
  const ftaPosition = tv2State?.attributes?.media_position;
  const ftaDuration = tv2State?.attributes?.media_duration;

  // PR T2 — Source switcher. Dispatches media_player.select_source on
  // the room's TV entity. Works universally for LG WebOS + Sony BRAVIA
  // (both consume a source name from their respective source_list);
  // the picker reads source_list live from hass.states so each TV
  // surfaces its own vocabulary (LG includes apps + HDMI, Sony just
  // HDMI inputs typically).
  const onSelectSource = (source) => {
    if (!source) return;
    hass?.callService('media_player', 'select_source', {
      entity_id: `media_player.${room.id}_tv`,
      source,
    });
    setSourcePickerOpen(false);
  };

  // TV-native remote commands — drives remote.<room>_tv (NOT
  // remote.apple_tv_<room>). Used for navigating the TV's own UI
  // when a software update / setting dialog / app installation needs
  // OK / Back / d-pad input that the Apple TV can't see.
  const tvRemote = (action) => dispatchTVRemote(hass, room.id, action);

  // ── Scrub tier ───────────────────────────────────────────────────────
  //
  // There is no single scrub that works everywhere, because the apps
  // publish different things. Measured over five days of recorded
  // history, 2026-11-01: Disney+ publishes a real media_duration and
  // title in both rooms; Netflix publishes neither, in any row, in
  // either room; HBO Max showed only a 30 s duration with an empty
  // title (probably a preview loop, so still unresolved); Kayo
  // publishes none because it is live. media_position could NOT be
  // judged from that history at all — MediaPlayerEntity excludes it
  // from the recorder — so the tier is decided from live state here,
  // never from history. See [[media-position-is-unrecorded]] and
  // [[atv-apps-differ-on-duration]].
  //
  // Tier A (duration + position live): a scrub bar plus one skip pair.
  // Tier B (anything else): two skip pairs in multiples of the app's
  // own step, which is the only unit available.
  //
  // The tier only shapes the Playback row. It never changes what the
  // touchpad does: the pad used to scrub on a horizontal swipe when
  // this said a programme was playing or paused, and HA's now-playing
  // cannot say what is on screen. It took the pad's left / right away
  // three times on three different false positives: a Disney+ browse
  // preview, an HBO Max preview the Apple TV kept re-reporting after
  // the app closed (both 2026-11-01), and a Disney+ film paused the
  // night before, re-reported at wake while the Home Screen was up
  // (Ben, 2026-11-05). There is no foreground-app signal to gate on,
  // so the swipe-scrub was removed rather than guarded a fourth time.
  const atvPlaying = appleTv?.state === 'playing';
  const scrubDuration = appleTv?.mediaDuration > 0 ? appleTv.mediaDuration : null;
  const isPreview = scrubDuration != null && scrubDuration <= PREVIEW_MAX_S;
  const scrubbable = (atvPlaying || appleTv?.state === 'paused') && !isPreview;
  const canSeek = scrubbable && scrubDuration != null && appleTv?.mediaPosition != null;

  // Seek, then check whether it took. Used by Restart and the scrub bar.
  const seekWithCheck = (seconds, label) => {
    const target = Math.round(seconds);
    const before = appleTv?.mediaPosition;
    appleTvCommand?.(room.id, 'seek', target);
    press('seek');
    setSeekNotice(null);
    clearTimeout(seekCheckRef.current.check);
    clearTimeout(seekCheckRef.current.expire);

    const looksIgnored = () => {
      const after = atvRef.current?.mediaPosition;
      // No position at all, or none before the press: nothing to
      // compare, so say nothing rather than guess.
      if (after == null || before == null) return false;
      // Moved to roughly the target — it took.
      if (Math.abs(after - target) <= SEEK_TOLERANCE_S) return false;
      // Still roughly where it was, i.e. the stream carried on as if
      // the seek never happened.
      return Math.abs(after - before) <= SEEK_TOLERANCE_S;
    };

    const sample = (remaining) => {
      if (!looksIgnored()) return;
      if (remaining > 0) {
        seekCheckRef.current.check = setTimeout(() => sample(remaining - 1), SEEK_SETTLE_MS);
        return;
      }
      setSeekNotice(`${label} didn't take — this app ignores a seek. Hold the touchpad for its own menu.`);
      seekCheckRef.current.expire = setTimeout(() => setSeekNotice(null), SEEK_NOTICE_MS);
    };
    seekCheckRef.current.check = setTimeout(() => sample(1), SEEK_SETTLE_MS);
  };

  // One skip, or N of them in a row. num_repeats is HA's own, so the
  // repeat loop runs server-side rather than as N round trips.
  const skipBy = (cmd, steps) => {
    if (steps <= 1) { sendRemoteCommand?.(room.id, cmd); return; }
    sendRemoteCommand?.(room.id, cmd, { numRepeats: steps, delaySecs: steps >= 30 ? 0.05 : 0.1 });
  };

  const cancelHold = () => {
    clearTimeout(holdRef.current.ring);
    clearTimeout(holdRef.current.fire);
    holdRef.current = { ring: null, fire: null };
    setHoldPhase(null);
  };

  // Touchpad — swipe to navigate (one press per STEP_PX crossed), tap
  // to select, hold to long-press. Axis locks at first crossing. A swipe
  // is navigation in every context; during playback a left / right is
  // the app's own 10 s skip, the same as a Siri Remote's.
  const onPadPointerDown = (e) => {
    if (!isOn) return;
    const r = padRef.current?.getBoundingClientRect();
    if (!r) return;
    startRef.current = {
      startX: e.clientX, startY: e.clientY,
      lastX: e.clientX, lastY: e.clientY,
      t: Date.now(), axis: null, steps: 0, held: false,
    };
    setRipple({ x: e.clientX - r.left, y: e.clientY - r.top, id: Date.now() });

    // Long-press. The ring is late so it never flashes on a tap; the
    // held press follows at HOLD_MS. `held` suppresses the tap-select
    // on release — one gesture, one command.
    cancelHold();
    holdRef.current.ring = setTimeout(() => setHoldPhase('arming'), HOLD_RING_MS);
    holdRef.current.fire = setTimeout(() => {
      const st = startRef.current;
      if (!st || st.axis || st.steps > 0) return;
      st.held = true;
      setHoldPhase('held');
      press('select');
      sendRemoteCommand?.(room.id, 'select', { holdSecs: 1 });
    }, HOLD_MS);
  };

  const onPadPointerMove = (e) => {
    const st = startRef.current;
    if (!isOn || !st) return;
    const dxTotal = e.clientX - st.startX;
    const dyTotal = e.clientY - st.startY;
    if (Math.max(Math.abs(dxTotal), Math.abs(dyTotal)) > HOLD_SLOP_PX) cancelHold();
    if (st.held) return;
    if (!st.axis && Math.max(Math.abs(dxTotal), Math.abs(dyTotal)) > 10) {
      st.axis = Math.abs(dxTotal) > Math.abs(dyTotal) ? 'x' : 'y';
    }
    if (!st.axis) return;

    const delta = st.axis === 'x' ? e.clientX - st.lastX : e.clientY - st.lastY;
    if (Math.abs(delta) < STEP_PX) return;
    const dir = st.axis === 'x'
      ? (delta > 0 ? 'right' : 'left')
      : (delta > 0 ? 'down'  : 'up');
    dispatch(dir);
    st.steps += 1;
    st.lastX = e.clientX;
    st.lastY = e.clientY;
  };

  const onPadPointerUp = (e) => {
    const st = startRef.current;
    startRef.current = null;
    cancelHold();
    if (!isOn || !st) return;
    // The held press already went out on the timer.
    if (st.held) return;

    if (st.steps > 0) return;
    const dx = e.clientX - st.startX;
    const dy = e.clientY - st.startY;
    // A finger that did not travel is a tap, however long it rested.
    // There used to be a `dt < 350` on this, which left everything
    // between 350 ms and HOLD_MS falling through to the direction
    // dispatch below — where dx and dy are both ~0, so it sent `up` on
    // sub-pixel noise. A hold aimed at HOLD_MS but released a little
    // early therefore moved the selection instead of doing nothing,
    // which is what Ben saw as the long-press "selecting areas of the
    // screen" (2026-11-01). The genuine hold has already returned
    // above on st.held, so nothing here needs to know about duration.
    if (Math.abs(dx) < 8 && Math.abs(dy) < 8) { dispatch('select'); return; }
    if (Math.abs(dx) > Math.abs(dy)) dispatch(dx > 0 ? 'right' : 'left');
    else dispatch(dy > 0 ? 'down' : 'up');
  };

  const onPadPointerCancel = () => {
    startRef.current = null;
    cancelHold();
  };

  // TV brand for the now-playing strip badge (BRAVIA / LG). User
  // feedback 2026-05-25 — the brand mark previously lived in the
  // shell eyebrow at the top, but it belongs on the strip itself
  // alongside the Apple TV cover/title so it's adjacent to "what's
  // currently showing".
  const tvBrand = tvBrandFor(room.id);

  // Now-playing copy mirrors the Screen tile's precedence, including
  // the in-flight case: the ring catches the eye, the words say which
  // direction. Without them the strip sat on "Standby · Tap power to
  // wake" for the whole wake chain, which reads as a press that didn't
  // land.
  //
  // Title and app show only while the Apple TV is `playing` / `paused`.
  // An `idle` Apple TV keeps its last `app_name` for hours, and a deaf
  // integration session (ha-av HANDOFF §4 item 20) sits at `idle` while
  // something plays, so "HBO Max · Now playing" was routinely wrong
  // (Rumpus, 2026-10-26, during DAZN). Idle blanks the strip — Ben's
  // call, 2026-10-26. The non-breaking spaces hold the card's height.
  // A 26 s session with no title is an app's autoplaying preview, not
  // something the room is watching. Left ungated, the strip read
  // "HBO Max / Now playing" on the Apple TV home screen long after HBO
  // Max was closed, because its preview session kept resurfacing
  // (Ben, 2026-11-01; visible in history as repeated `paused HBO Max
  // 26` rows). Same PREVIEW_MAX_S the playback row uses.
  const atvActive = (appleTv?.state === 'playing' || appleTv?.state === 'paused') && !isPreview;
  const nowTitle = powerBusy ? (powerIntent === 'on' ? 'Turning on…' : 'Turning off…')
    : !isOn ? 'Standby'
    : !atvActive ? ' '
    : appleTv?.title ? appleTv.title
    : appleTv?.app   ? appleTv.app
    : ' ';
  const nowSub = powerBusy ? (powerSlow ? 'still working' : 'TV · Apple TV · Amp')
    : !isOn ? 'Tap power to wake'
    : !atvActive ? ' '
    : appleTv?.title ? (appleTv.artist || appleTv.app || 'Apple TV+')
    : appleTv?.app   ? 'Now playing'
    : ' ';

  // Primary volume routing — amp-primary for Pavilion / Rumpus (their
  // Sonos feeds external amps); Sonos-primary otherwise. Pool extension
  // shows a second rocker when active for this room.
  const primaryEntity = primaryEntityFor(room);
  const primaryLabel  = primaryLabelFor(room);
  const primaryLive   = primaryEntity ? mediaPlayer?.(primaryEntity) : null;
  const ourPoolActive = room.id === 'pavilion' ? poolExtension?.pavilionActive
                      : room.id === 'rumpus'   ? poolExtension?.rumpusActive
                      : false;
  const poolLive = ourPoolActive ? mediaPlayer?.('media_player.drx_5_zone_3') : null;
  const showVolLabel = !!(poolLive && primaryLive);

  const isPhone = viewport === 'phone';
  const freeTv = isBravia && tvMode === 'freetv';

  // Shared 2026-10 vocabulary for this surface. A remote key at rest is a
  // quiet cardSoft fill with no outline; a key whose code just went out
  // flashes the selected fill for the 220 ms `pressed` window.
  const keyStyle = (active) => ({
    border: 'none', padding: 0,
    background: active ? T.selected : T.cardSoft,
    color: active ? T.selectedInk : (isOn ? T.ink : T.mute),
    borderRadius: RADIUS.control, cursor: isOn ? 'pointer' : 'not-allowed',
    transition: 'background .15s, color .15s',
  });
  const roundQuiet = (size = 44) => ({
    width: size, height: size, borderRadius: '50%', border: 'none', padding: 0,
    background: T.cardSoft, color: T.ink, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  });
  const closeGlyph = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M6 6l12 12M18 6L6 18"/>
    </svg>
  );
  const keyboardGlyph = (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="6" width="18" height="12" rx="2"/>
      <path d="M7 10h.01M11 10h.01M15 10h.01M7 14h10"/>
    </svg>
  );
  // The remote's own column (chrome rows + touchpad / keypad) is held
  // to the touchpad's width on tablet so the whole thing reads as one
  // handset; the TV menu inset spans the card.
  const remoteCol = {
    width: '100%', maxWidth: isPhone ? '100%' : 420, margin: '0 auto',
    // iOS raises its own text-selection UI on a long press, which is
    // exactly the gesture the touchpad asks for: Ben's screenshot of a
    // near-miss hold shows selection handles thrown across the pad and
    // the TV button. `userSelect` alone does not stop it — Safari
    // needs the -webkit- pair, and the callout suppression as well.
    // Scoped to the remote rather than the app, so text stays
    // selectable everywhere else.
    userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none',
  };

  return (
    <TakeoverShell T={T} viewport={viewport}
      eyebrow={tv?.source && tv.source !== '—' ? tv.source : 'Apple TV'}
      title={room.name}
      onClose={onClose}
      bodyless>

      <div style={{
        // bodyless TakeoverShell: own inner padding, add safe-area-bottom
        // so home-indicator strip doesn't crowd the bottom row of controls.
        padding: isPhone ? '18px 14px calc(32px + env(safe-area-inset-bottom))'
                         : '24px 36px calc(40px + env(safe-area-inset-bottom))',
        display: 'flex', flexDirection: 'column', gap: 16,
        maxWidth: 760, margin: '0 auto', width: '100%',
        // box-sizing: border-box so the 100% width *includes* the padding.
        // Without this the inner content area is viewport + 2× padding wide,
        // and any flex row with justify-content: space-between (chrome rows,
        // volume rocker, destinations chips) pushes its outer children past
        // the viewport edges — buttons + the volume readout get clipped on
        // narrow viewports.
        boxSizing: 'border-box',
      }}>

        {/* Apple TV ↔ Free TV mode segment. BRAVIA rooms only —
            Pavilion (LG) doesn't share this two-mode model and the
            FTA path for webostv would need its own command vocabulary.
            Selecting a pill fires the BRAVIA source switch. Mode
            follows live state, so a physical-remote source change
            updates the pill in turn. See block above shell render
            for detection details. Drawn as the panel's Segmented tray
            (built inline to keep each option's aria-label and the
            disabled-in-standby state). */}
        {isBravia && (
          <div style={{
            display: 'flex', padding: 3,
            background: T.cardSoft, borderRadius: RADIUS.control,
          }}>
            {[
              { id: 'appletv', label: 'Apple TV', onClick: switchToAppleTv },
              { id: 'freetv',  label: 'Free TV',  onClick: switchToFreeTv },
            ].map(({ id, label, onClick }) => {
              const active = tvMode === id;
              return (
                <button key={id} type="button" onClick={onClick}
                  aria-label={`Switch to ${label}`}
                  aria-pressed={active}
                  disabled={!isOn}
                  style={{
                    flex: 1, minWidth: 0, height: 48, padding: '0 12px', border: 'none',
                    background: active ? T.selected : 'transparent',
                    color: active ? T.selectedInk : (isOn ? T.ink : T.mute),
                    borderRadius: RADIUS.key,
                    cursor: isOn ? 'pointer' : 'not-allowed',
                    fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
                    whiteSpace: 'nowrap',
                    transition: 'background .18s ease-out, color .18s ease-out',
                  }}>
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Now-playing strip — Apple TV mode only. In Free TV mode
            the FTA channel card below carries this role. */}
        {!freeTv && (
          <Card T={T} style={{
            padding: isPhone ? 14 : 16, flexDirection: 'row', gap: 16, alignItems: 'center',
            // Don't dim mid-press: the strip is carrying the "Turning
            // on…" copy at that point and needs to be readable.
            opacity: isOn || powerBusy ? 1 : .55,
          }}>
            <div style={{ width: 72, height: 72, flexShrink: 0, display: 'flex' }}>
              {atvActive && appleTvCoverUrl
                ? <img src={appleTvCoverUrl} alt=""
                       style={{ width: '100%', height: '100%', borderRadius: RADIUS.key, objectFit: 'cover', background: T.cardSoft, display: 'block' }}/>
                : <AppleTvMark radius={RADIUS.key} style={{ boxShadow: 'none' }}/>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <ALabel T={T}>
                {tv?.source && tv.source !== '—' ? tv.source : 'Apple TV'}
              </ALabel>
              <div style={{
                fontFamily: A_FONT.serif, fontSize: fs(T, 26), fontWeight: 500,
                color: isOn && !powerBusy ? T.ink : T.inkSoft,
                marginTop: 4, lineHeight: 1.1,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>
                {nowTitle}
              </div>
              <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.inkSoft, marginTop: 4 }}>
                {nowSub}
              </div>
            </div>
          </Card>
        )}

        {/* Free TV channel card — only in FTA mode. Shows live channel
            number, program title, and EPG progress through the time
            slot. All values come from media_player.<room>_tv_2 (the
            braviatv entity) — the androidtv_remote entity reports the
            current Android app (e.g. com.sony.dtv.tvx) but not the
            channel itself. */}
        {freeTv && (
          <Card T={T} eyebrow="Free TV" style={{
            padding: isPhone ? '16px 18px' : '18px 22px', gap: 10,
            opacity: isOn ? 1 : .55,
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
              <div style={{
                fontFamily: A_FONT.serif, fontSize: fs(T, 44), fontWeight: 500, lineHeight: 1,
                color: T.ink, minWidth: 48,
                fontVariantNumeric: 'lining-nums tabular-nums',
              }}>
                {ftaChannel || '—'}
              </div>
              <div style={{
                flex: 1, minWidth: 0,
                fontFamily: A_FONT.serif, fontSize: fs(T, 26),
                color: ftaTitle || isOn ? T.ink : T.inkSoft, fontWeight: 500, lineHeight: 1.1,
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}>
                {ftaTitle || (isOn ? 'Free TV' : 'Standby')}
              </div>
            </div>
            {ftaPosition != null && ftaDuration > 0 && (
              <div>
                <div style={{
                  width: '100%', height: 4, background: T.cardSoft, borderRadius: 2,
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${Math.min(100, (ftaPosition / ftaDuration) * 100)}%`,
                    height: '100%', background: T.live, borderRadius: 2,
                    transition: 'width 1s linear',
                  }}/>
                </div>
                <div style={{
                  marginTop: 6,
                  fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {Math.floor(ftaPosition / 60)}m {Math.round(ftaPosition % 60)}s
                  {' / '}
                  {Math.floor(ftaDuration / 60)}m
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Keyboard input bar — shown while the Apple TV has a text
            field focused (plus the KB_HOLD_MS grace). Text goes to the
            TV as you type (apple_tv.set_keyboard_text, debounced); Done
            sends anything pending and drops the device keyboard so the
            touchpad is reachable again. */}
        {kbOpen && (
          <form onSubmit={(e) => {
            e.preventDefault();
            flushKb(room.id);
            kbInputRef.current?.blur();
          }} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: T.card, borderRadius: RADIUS.card, boxShadow: T.cardShadow,
            padding: isPhone ? '8px 8px 8px 14px' : '10px 10px 10px 18px',
            animation: 'a-pop .18s ease',
          }}>
            <span aria-hidden style={{
              display: 'flex', alignItems: 'center', flexShrink: 0,
              color: T.inkSoft,
            }}>{keyboardGlyph}</span>
            <input ref={kbInputRef} value={kbText}
              onChange={(e) => { setKbText(e.target.value); queueKb(room.id, e.target.value); }}
              autoComplete="off" autoCapitalize="off" autoCorrect="off" spellCheck={false}
              enterKeyHint="done"
              placeholder="Type for Apple TV…"
              style={{
                flex: 1, minWidth: 0, height: 44, border: 'none', background: 'transparent',
                color: T.ink, fontFamily: A_FONT.sans, fontSize: fs(T, 16), fontWeight: 500,
                outline: 'none', padding: '0 4px',
              }}/>
            <button type="button" onClick={() => {
              setKbText('');
              queueKb(room.id, '');
              flushKb(room.id);
            }}
              aria-label="Clear text"
              style={{ ...roundQuiet(44), color: T.ink, cursor: 'pointer' }}>{closeGlyph}</button>
            <button type="submit" aria-label="Done"
              style={{
                ...roundQuiet(44),
                background: kbText ? T.primary : T.cardSoft,
                color: kbText ? T.primaryInk : T.ink,
                cursor: 'pointer',
              }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12.5l4.5 4.5L19 7.5"/>
              </svg>
            </button>
          </form>
        )}

        {/* The remote itself — chrome rows, TV menu, touchpad or FTA
            keypad — on one card, so its quiet keys sit on a surface
            rather than on the page. */}
        <Card T={T} style={{
          padding: isPhone ? '16px 14px' : '22px 24px', gap: 18,
        }}>

        {/* Top chrome row — 4 buttons, mirroring the count of the
            bottom row (Play/Pause / Keyboard / Audio / Siri). Order:
            Menu / Home / TV menu / Power. The TV-menu toggle sits to
            the immediate left of Power so the row reads as a single
            symmetric strip rather than two clusters. Power dispatches
            the whole-stack Watch / Off scripts (AV-aware); falls back
            to mock-only update() when the room has no av mapping. */}
        <div style={{ ...remoteCol, display: 'flex', justifyContent: 'space-between' }}>
          <ChromeBtn T={T} k="menu" pressed={pressed} onClick={() => dispatch('menu')} disabled={!isOn}
                     onPress={press} ariaLabel="Menu">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </ChromeBtn>
          <ChromeBtn T={T} k="home" pressed={pressed} onClick={() => dispatch('home')} disabled={!isOn}
                     onPress={press} ariaLabel="Home">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="12" rx="2"/><path d="M8 21h8M12 17v4"/>
            </svg>
          </ChromeBtn>
          <ChromeBtn T={T} k="tvmenu" pressed={pressed} active={tvMenuOpen}
                     onPress={press}
                     onClick={() => setTvMenuOpen((o) => !o)}
                     ariaLabel={tvMenuOpen ? 'Close TV menu' : 'Open TV menu'}>
            <span style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: 1, lineHeight: 1,
            }}>
              <span style={{
                fontFamily: A_FONT.sans, fontSize: fs(T, 13), fontWeight: 600,
              }}>TV</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round"
                   style={{
                     transform: tvMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                     transition: 'transform .2s',
                   }}>
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </span>
          </ChromeBtn>
          {/* Power is the only button in this row that starts a chain
              rather than sending a code, so it carries the busy
              affordance instead of the selected flash. It is the shared
              PowerButton, so it reads exactly like the room tile's
              Screen power: primary fill when the stack is awake, busy
              ring + halo while the chain runs. */}
          <PowerButton T={T} size={52} on={isOn} busy={powerBusy}
                       onClick={pressPower}
                       ariaLabel={powerLabel}/>
        </div>

        {/* TV menu disclosure — inline TV-native d-pad. Sits above the
            touchpad now (toggle lives in the top chrome row), so when
            open it pushes the touchpad down rather than appearing at
            the bottom of the takeover. */}
        <TVMenuDisclosure T={T} open={tvMenuOpen}
          onAction={tvRemote}
          tvBrand={tvBrand}
          appleTv={appleTv}
          compact={isPhone}
          onReturnToAppleTV={room.av ? () => act?.(room.id, 'watch') : null}/>

        {freeTv ? (
          /* Free TV controls — channel up/down stack + digit pad +
              Guide / Info. All via remote.send_command on the braviatv
              entity (PascalCase Sony IRCC codes). When FTA mode is
              active the touchpad is hidden since d-pad navigation
              doesn't apply to channel browsing. */
          <div style={{
            ...remoteCol,
            display: 'grid',
            gridTemplateColumns: isPhone ? '56px 1fr 56px' : '64px 1fr 64px',
            gap: 8,
            alignItems: 'stretch',
          }}>
            {/* CH up/down stack. keyStyle() flashes the selected fill on
                press (the `pressed` state briefly equals the IRCC code,
                then clears after 220ms). */}
            {(() => null)()}
            {(() => null)()}
            {/* (inline button styles use a shared helper defined inline below) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { cmd: 'ChannelUp',   label: 'Channel up',   icon: 'M6 15l6-6 6 6', position: 'above' },
                { cmd: 'ChannelDown', label: 'Channel down', icon: 'M6 9l6 6 6-6', position: 'below' },
              ].map(({ cmd, label, icon, position }) => {
                const active = pressed === cmd;
                const chLabel = (
                  <span style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), fontWeight: 500, color: active ? T.selectedInk : T.inkSoft }}>CH</span>
                );
                return (
                  <button key={cmd} type="button"
                    onClick={() => ftaCommand(cmd)} disabled={!isOn}
                    aria-label={label}
                    style={{
                      ...keyStyle(active),
                      width: '100%', flex: 1, minHeight: 60,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', gap: 4,
                    }}>
                    {position === 'above'
                      ? <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d={icon}/></svg>
                      : chLabel}
                    {position === 'above'
                      ? chLabel
                      : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d={icon}/></svg>}
                  </button>
                );
              })}
            </div>

            {/* Digit pad — 3x4 grid: 1-9 in rows 1-3, then 0 centred
                on row 4 (no * / # since neither FTA nor most BRAVIA
                remotes ship them; the BRAVIA's tuner takes direct
                channel input by digit-then-pause). */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 8,
            }}>
              {[1,2,3,4,5,6,7,8,9].map(n => {
                const cmd = `Num${n}`;
                const active = pressed === cmd;
                return (
                  <button key={n} type="button"
                    onClick={() => ftaCommand(cmd)} disabled={!isOn}
                    aria-label={`Channel ${n}`}
                    style={{
                      ...keyStyle(active),
                      minHeight: 56,
                      fontFamily: A_FONT.serif, fontSize: fs(T, 28),
                      fontWeight: 500, lineHeight: 1,
                      fontVariantNumeric: 'lining-nums tabular-nums',
                    }}>{n}</button>
                );
              })}
              <div/>
              {(() => {
                const cmd = 'Num0';
                const active = pressed === cmd;
                return (
                  <button type="button"
                    onClick={() => ftaCommand(cmd)} disabled={!isOn}
                    aria-label="Channel 0"
                    style={{
                      ...keyStyle(active),
                      minHeight: 56,
                      fontFamily: A_FONT.serif, fontSize: fs(T, 28),
                      fontWeight: 500, lineHeight: 1,
                      fontVariantNumeric: 'lining-nums tabular-nums',
                    }}>0</button>
                );
              })()}
              <div/>
            </div>

            {/* Guide / Info stack. Sony IRCC vocabulary varies by
                firmware — `Guide` and `GGuide` were both rejected on
                Office (2026-05-27); `EPG` is the next candidate. If
                this fails too, fallback would be the androidtv_remote
                keyevent path with `KEYCODE_GUIDE` instead. Info uses
                `Display` (info overlay), which is universal. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { cmd: 'EPG',     label: 'Guide' },
                { cmd: 'Display', label: 'Info'  },
              ].map(({ cmd, label }) => {
                const active = pressed === cmd;
                return (
                  <button key={cmd} type="button"
                    onClick={() => ftaCommand(cmd)} disabled={!isOn}
                    aria-label={label}
                    style={{
                      ...keyStyle(active),
                      width: '100%', flex: 1, minHeight: 60,
                      fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
                    }}>{label}</button>
                );
              })}
            </div>
          </div>
        ) : (
          /* Gesture touchpad — swipe to navigate, tap to select.
              Phone fills the available width (just the container's 14px
              side padding as the gap to viewport edges) and stays square,
              so it's a finger-friendly target without dominating vertical
              space. Tablet+ caps at 420 and uses 4:5 (closer to the real
              Apple TV remote proportions). The touchpad's touchAction:'none'
              traps swipe-down within it; the surrounding takeover scrolls
              as long as there's content above/below to reach. */
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div ref={padRef}
              onPointerDown={onPadPointerDown}
              onPointerMove={onPadPointerMove}
              onPointerUp={onPadPointerUp}
              onPointerCancel={onPadPointerCancel}
              style={{
                width: '100%',
                maxWidth: isPhone ? '100%' : 420,
                aspectRatio: isPhone ? '1 / 1' : '4 / 5',
                borderRadius: 28,
                // The remote's physical glass: always dark, in either
                // theme, so it keeps its own material colours rather
                // than theme roles.
                background: 'linear-gradient(180deg, #2c2925 0%, #15120f 100%)',
                position: 'relative', overflow: 'hidden',
                cursor: isOn ? 'pointer' : 'not-allowed',
                opacity: isOn ? 1 : .5,
                touchAction: 'none',
                userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,.07), 0 6px 18px rgba(0,0,0,.16)',
                color: 'rgba(255,255,255,.5)',
              }}>
              {[
                { dir: 'up',    style: { top: 0,    left: '50%', transform: 'translateX(-50%)' }, d: 'M6 15l6-6 6 6' },
                { dir: 'down',  style: { bottom: 0, left: '50%', transform: 'translateX(-50%)' }, d: 'M6 9l6 6 6-6' },
                { dir: 'left',  style: { left: 0,   top: '50%',  transform: 'translateY(-50%)' }, d: 'M15 6l-6 6 6 6' },
                { dir: 'right', style: { right: 0,  top: '50%',  transform: 'translateY(-50%)' }, d: 'M9 6l6 6-6 6' },
              ].map(({ dir, style, d }) => (
                <button key={dir}
                  onClick={(e) => { e.stopPropagation(); dispatch(dir); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onPointerMove={(e) => e.stopPropagation()}
                  onPointerUp={(e) => e.stopPropagation()}
                  disabled={!isOn}
                  aria-label={dir}
                  style={{
                    position: 'absolute', ...style,
                    width: 48, height: 48, padding: 0, border: 'none', background: 'transparent',
                    color: 'rgba(255,255,255,.5)', cursor: isOn ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d={d}/>
                  </svg>
                </button>
              ))}
              {/* Press glow — a pale wash of the glass, not a theme
                  role: the pad is dark in both themes, so the busy wash
                  (dark ink at 7% in light) never showed on it. */}
              {pressed && ['up','down','left','right','select'].includes(pressed) && (
                <span style={{
                  position: 'absolute', inset: 0, pointerEvents: 'none',
                  background: pressed === 'select'
                    ? 'radial-gradient(circle at center, rgba(255,255,255,.12) 0%, transparent 60%)'
                    : pressed === 'up' ? 'linear-gradient(180deg, rgba(255,255,255,.12) 0%, transparent 50%)'
                    : pressed === 'down' ? 'linear-gradient(0deg, rgba(255,255,255,.12) 0%, transparent 50%)'
                    : pressed === 'left' ? 'linear-gradient(90deg, rgba(255,255,255,.12) 0%, transparent 50%)'
                    : 'linear-gradient(270deg, rgba(255,255,255,.12) 0%, transparent 50%)',
                  transition: 'opacity .2s',
                }}/>
              )}
              {ripple && (
                <span key={ripple.id} style={{
                  position: 'absolute', left: ripple.x - 30, top: ripple.y - 30,
                  width: 60, height: 60, borderRadius: '50%',
                  background: 'rgba(255,255,255,.14)', opacity: .6,
                  animation: 'a-ripple .55s ease-out forwards', pointerEvents: 'none',
                }}/>
              )}
              {/* Long-press ring. Arms at HOLD_RING_MS so a tap never
                  draws it, and the sweep runs the remaining window. On
                  'held' it sits full, which is the only confirmation
                  available: the Apple TV reports nothing back about a
                  held press. */}
              {holdPhase && (
                <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden="true"
                  style={{
                    position: 'absolute', left: '50%', top: '50%',
                    transform: 'translate(-50%, -50%) rotate(-90deg)',
                    pointerEvents: 'none',
                  }}>
                  <circle cx="24" cy="24" r="20" fill="none"
                          stroke="rgba(255,255,255,.16)" strokeWidth="2.5"/>
                  <circle cx="24" cy="24" r="20" fill="none"
                          stroke="rgba(255,255,255,.85)" strokeWidth="2.5"
                          strokeLinecap="round" strokeDasharray="126"
                          style={holdPhase === 'held'
                            ? { strokeDashoffset: 0 }
                            : { animation: `a-hold ${HOLD_MS - HOLD_RING_MS}ms linear forwards` }}/>
                </svg>
              )}
            </div>
          </div>
        )}

        {/* Playback — directly under the trackpad, inside the remote's
            own column, because it acts on what the pad is driving.
            Deliberately NOT a Card: the eyebrow and card padding it
            used to carry pushed everything below it down the page
            (Ben, 2026-11-01), and it belongs to the remote rather than
            standing beside it.

            Shown only while a real programme is playing or paused and
            the screen is awake. `scrubbable` excludes trailers, which
            is what stops a Playback row appearing over the Apple TV
            app's autoplaying home-screen previews.

            Tiered on what the app publishes. With a timeline: a bar
            for long jumps, one skip pair, Restart. Without one: two
            skip pairs, because there is no bar to make a big jump
            with and the app's own step is all of 10 s — the wide pair
            sends six of them. No app name is printed anywhere here:
            `app_name` comes from a different source than the playback
            data and was observed reading "HBO Max" over an Apple TV
            trailer, so it cannot be trusted to label this. */}
        {!freeTv && isOn && scrubbable && (
          <div style={{ ...remoteCol, gap: 8, alignItems: 'center' }}>
            {canSeek && (
              <Scrubber T={T}
                position={appleTv?.mediaPosition}
                duration={appleTv?.mediaDuration}
                updatedAt={appleTv?.mediaPositionUpdatedAt}
                playing={atvPlaying}
                onSeek={(secs) => seekWithCheck(secs, 'Scrub')}/>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
              {!canSeek && SKIP_STEPS.map((n) => (
                <SkipKey key={`b${n}`} T={T} isOn={isOn} steps={n} label={`Back ${n} steps`}
                         active={pressed === `skip_backward_${n}`}
                         onClick={() => { press(`skip_backward_${n}`); skipBy('skip_backward', n); }}/>
              )).reverse()}
              {canSeek && (
                <SkipKey T={T} isOn={isOn} steps={1} label="Skip back"
                         active={pressed === 'skip_backward_1'}
                         onClick={() => { press('skip_backward_1'); skipBy('skip_backward', 1); }}/>
              )}
              {scrubDuration != null && (
                <button onClick={() => seekWithCheck(0, 'Restart')} disabled={!isOn}
                  style={{
                    ...keyStyle(pressed === 'seek'),
                    height: 44, padding: '0 16px',
                    fontFamily: A_FONT.sans, fontSize: fs(T, 14), fontWeight: 500,
                    display: 'flex', alignItems: 'center', gap: 7,
                  }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 1 0 3-6.7M3 4v5h5"/>
                  </svg>
                  Restart
                </button>
              )}
              {canSeek && (
                <SkipKey T={T} isOn={isOn} fwd steps={1} label="Skip forward"
                         active={pressed === 'skip_forward_1'}
                         onClick={() => { press('skip_forward_1'); skipBy('skip_forward', 1); }}/>
              )}
              {!canSeek && SKIP_STEPS.map((n) => (
                <SkipKey key={`f${n}`} T={T} isOn={isOn} fwd steps={n} label={`Forward ${n} steps`}
                         active={pressed === `skip_forward_${n}`}
                         onClick={() => { press(`skip_forward_${n}`); skipBy('skip_forward', n); }}/>
              ))}
            </div>
            {!canSeek && (
              <div style={{
                fontFamily: A_FONT.sans, fontSize: fs(T, 12), color: T.mute,
                textAlign: 'center', lineHeight: 1.3,
              }}>
                {'No timeline from this app \u00b7 the pairs send 1, 6 and 30 of its own steps'}
              </div>
            )}
            {seekNotice && (
              <div style={{
                fontFamily: A_FONT.sans, fontSize: fs(T, 12), color: T.inkSoft,
                textAlign: 'center', lineHeight: 1.3,
              }}>
                {seekNotice}
              </div>
            )}
          </div>
        )}

        {/* Bottom chrome row: Apple-TV-specific buttons (Play/Pause,
            Siri) hidden in Free TV mode — they don't map to
            anything meaningful when watching the tuner. Audio stays
            because the amp settings are relevant in either mode, and
            sits centred under the keypad when it is alone. */}
        {(!freeTv || hasAudio) && (
        <div style={{ ...remoteCol, display: 'flex', justifyContent: freeTv ? 'center' : 'space-between' }}>
          {!freeTv && (
            <ChromeBtn T={T} k="play_pause" pressed={pressed} disabled={!isOn}
                       onPress={press} onClick={() => dispatch('play_pause')} ariaLabel="Play/Pause">
              {appleTv?.state === 'playing'
                ? <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>
                : <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>}
            </ChromeBtn>
          )}
          {hasAudio && (
            <ChromeBtn T={T} k="audio" pressed={pressed} onPress={press}
                       active={audioOpen}
                       onClick={() => setAudioOpen((o) => !o)} ariaLabel="Audio settings">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12v-2M5 14v0M10 12v-4M10 16v-1M15 12v-6M15 18v-1M20 12v-3M20 15v0"/>
                <circle cx="5"  cy="13" r="1.4" fill="currentColor"/>
                <circle cx="10" cy="15.5" r="1.4" fill="currentColor"/>
                <circle cx="15" cy="17.5" r="1.4" fill="currentColor"/>
                <circle cx="20" cy="14" r="1.4" fill="currentColor"/>
              </svg>
            </ChromeBtn>
          )}
          {!freeTv && (
            <ChromeBtn T={T} k="siri" pressed={pressed} disabled={!isOn}
                       onPress={press} onClick={() => dispatch('siri')} ariaLabel="Siri">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="3" width="6" height="12" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/>
              </svg>
            </ChromeBtn>
          )}
        </div>
        )}
        </Card>

        {/* Volume rockers — primary + pool extension — and the
            destinations below them, on one card: everything about
            where the sound goes and how loud. */}
        {(primaryLive || poolLive || hasDestRow) && (
        <Card T={T} style={{ padding: isPhone ? '14px 14px' : '18px 24px', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {renderRocker(T, primaryLive, primaryEntity, primaryLabel, isOn, showVolLabel, sonosSpeakerCommand)}
          {renderRocker(T, poolLive, 'media_player.drx_5_zone_3', 'Pool', isOn, showVolLabel, sonosSpeakerCommand)}
        </div>

        {/* Destinations chips — Kitchen / Dining / Pool for the source
            room's AV stack. Unlike the music card, Pool is NOT filtered
            here — TV is AV-stack routing where Pavilion/Rumpus → Pool
            via DRX-5 zone 3 is the correct mechanism. Each is a quiet
            toggle: playing there takes the selected fill. */}
        {hasDestRow && (
          <div>
            <ALabel T={T} style={{ marginBottom: 10 }}>
              Playing in
            </ALabel>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(destinationsCommand ? room.destinations || [] : []).map((d) => {
                const isLocked = !!d.locked;
                return (
                  <button key={d.id}
                    disabled={isLocked && d.active}
                    onClick={() => {
                      if (isLocked) return;
                      destinationsCommand(room.id, d.id, !d.active);
                    }}
                    title={isLocked ? d.lockReason : undefined}
                    style={{
                      flex: '1 1 0', minWidth: 88, height: 48,
                      padding: '0 14px',
                      border: 'none',
                      background: d.active ? T.selected : T.cardSoft,
                      color: d.active ? T.selectedInk : T.ink,
                      borderRadius: RADIUS.control,
                      cursor: isLocked ? 'not-allowed' : 'pointer',
                      opacity: isLocked && !d.active ? 0.5 : 1,
                      fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
                      whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      transition: 'background .18s ease-out, color .18s ease-out',
                    }}>
                    {d.name}
                    {isLocked && d.active && (
                      <span style={{ fontSize: fs(T, 13), opacity: .75 }}>
                        · TV
                      </span>
                    )}
                  </button>
                );
              })}
              {tvToSonos && (
                <SonosRoomsChip T={T} info={tvToSonos} open={sonosRoomsOpen}
                  pending={sonosRoomsPending}
                  onToggle={() => setSonosRoomsOpen((o) => !o)}/>
              )}
            </div>
            {tvToSonos && sonosRoomsOpen && (
              <SonosRoomsPicker T={T} hass={hass} sourceId={room.id} info={tvToSonos}
                atvState={hass?.states?.[`media_player.apple_tv_${room.id}`]?.state}
                pending={sonosRoomsPending} setPending={setSonosRoomsPending}
                onClose={() => setSonosRoomsOpen(false)}/>
            )}
          </div>
        )}
        </Card>
        )}

        {/* Audio settings — bass / treble / centre / surround sliders.
            Opened via the Audio button in the bottom chrome row. */}
        {audioOpen && hasAudio && (audioCommand || eqCommand) && (
          <Card T={T} style={{
            padding: isPhone ? '16px 16px' : '20px 24px', gap: 10,
            animation: 'a-pop .18s ease',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <ALabel T={T}>Audio</ALabel>
              <span style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft }}>
                {eq?.owner || (audio?.centre ? 'DRX' : 'Sonos')}
              </span>
            </div>
            {[
              { key: 'centre',   label: 'Centre channel', c: audio?.centre,   cmd: audioCommand },
              { key: 'surround', label: 'Surround',       c: audio?.surround, cmd: audioCommand },
              { key: 'bass',     label: 'Bass',           c: eq?.bass,        cmd: eqCommand },
              { key: 'treble',   label: 'Treble',         c: eq?.treble,      cmd: eqCommand },
            ].filter((row) => row.c && row.cmd).map((row) => {
              const c = row.c;
              const fmt = (v) => `${v > 0 ? '+' : ''}${v}${c.unit ? ' ' + c.unit : ''}`;
              return (
                <div key={row.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 15), color: T.ink, fontWeight: 500 }}>
                      {row.label}
                    </span>
                    <span style={{
                      fontFamily: A_FONT.sans, fontSize: fs(T, 14), fontWeight: 500,
                      color: c.value === 0 ? T.inkSoft : T.ink, fontVariantNumeric: 'tabular-nums',
                    }}>
                      {fmt(c.value)}
                    </span>
                  </div>
                  <AudioSliderRow T={T} c={c} onChange={(v) => row.cmd(room.id, row.key, v)}/>
                </div>
              );
            })}
          </Card>
        )}
      </div>

      {/* SourcePicker overlay was previously triggered from the
          TV-menu card's [Source] button. Replaced 2026-05-25 with
          an [Apple TV] button that fires script.<room>_watch (the
          canonical "switch back to Apple TV" recipe). SourcePicker
          function + state stay defined so re-wiring is one prop +
          one button away if HDMI-input switching becomes a real
          need (Sony's androidtv_remote doesn't expose source_list
          anyway, so dropping it cost nothing for Office + Rumpus;
          LG's source picker would be useful on Pavilion only). */}
    </TakeoverShell>
  );
}
